import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  useTheme,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import PageSalesCta from '../components/PageSalesCta';

const Services = () => {
  const theme = useTheme();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching services from API...');
      
      const response = await apiService.getServices();
      console.log('📦 Full API Response:', response);
      
      // Debug: Check what we're actually getting
      console.log('🔍 Response type:', typeof response);
      console.log('📊 Is array?:', Array.isArray(response));
      console.log('🏷️ Response keys:', response ? Object.keys(response) : 'No response');
      
      // Handle the response - your PHP now returns array directly
      if (response && Array.isArray(response)) {
        console.log('✅ Services array received:', response);
        setServices(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback for nested structure
        console.log('✅ Services in response.data:', response.data);
        setServices(response.data);
      } else {
        console.warn('⚠️ No services data received or unexpected format:', response);
        setServices([]);
        setError('No services available');
      }
    } catch (error) {
      console.error('❌ Error fetching services:', error);
      console.error('Error details:', error.response || error.message);
      setError(`Failed to load services. Please check if the server is running. Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchServices();
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading services...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3,
        p: 3
      }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            Unable to Load Services
          </Typography>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleRetry}
          startIcon={<span>🔄</span>}
        >
          Try Again
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Check browser console for detailed error information.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Container maxWidth="lg">
        <motion.div 
          initial={{ opacity: 0, y: 50 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            textAlign="center" 
            gutterBottom 
            sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 700,
              mb: 2
            }}
          >
            What we build for you
          </Typography>
          <Typography 
            variant="h5" 
            textAlign="center" 
            sx={{ 
              mb: 6, 
              color: 'text.secondary', 
              maxWidth: 640, 
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Each service is about one thing: your business running smoother and your numbers looking better. NGOs, SMEs,
            campaigns, schools — pick what you need, then tell us your bottleneck. We scope fast.
          </Typography>
        </motion.div>

        {services.length > 0 ? (
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={service.id || index}>
                <motion.div 
                  initial={{ opacity: 0, y: 30 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ 
                    height: '100%', 
                    textAlign: 'center', 
                    p: 3, 
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-8px)', 
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    } 
                  }}>
                    <CardContent>
                      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {service.short_description || service.description}
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                        {service.featured && (
                          <Typography variant="caption" sx={{ 
                            backgroundColor: 'secondary.main', 
                            color: 'white', 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: '12px',
                            fontWeight: 600
                          }}>
                            Popular
                          </Typography>
                        )}
                        <Button component={Link} to="/request-quote" size="small" variant="contained" color="primary" sx={{ fontWeight: 700 }}>
                          Discuss this
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box textAlign="center" sx={{ mt: 8, p: 4 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No Services Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {services.length === 0 ? 'No services are currently available.' : 'Check your database connection.'}
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleRetry}
            >
              Refresh Services
            </Button>
          </Box>
        )}
      </Container>
      <PageSalesCta />
    </Box>
  );
};

export default Services;